import type {
  StorageAdapter,
  StorageStoreName,
  StorageTransaction,
  TransactionMode,
} from "./types.js";
import { STORAGE_SCHEMA_VERSION, STORAGE_STORES } from "./types.js";

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener("error", () => reject(request.error ?? new Error("IndexedDB request failed")), {
      once: true,
    });
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener("abort", () => reject(transaction.error ?? new Error("IndexedDB transaction aborted")), {
      once: true,
    });
    transaction.addEventListener("error", () => reject(transaction.error ?? new Error("IndexedDB transaction failed")), {
      once: true,
    });
  });
}

export interface IndexedDbStorageOptions {
  databaseName?: string;
  version?: number;
}

/** Browser implementation. One callback maps to one native IndexedDB transaction. */
export class IndexedDbStorageAdapter implements StorageAdapter {
  readonly #database: IDBDatabase;

  private constructor(database: IDBDatabase) {
    this.#database = database;
    database.addEventListener("versionchange", () => database.close());
  }

  static open(options: IndexedDbStorageOptions = {}): Promise<IndexedDbStorageAdapter> {
    if (!globalThis.indexedDB) return Promise.reject(new Error("IndexedDB is unavailable"));
    const request = globalThis.indexedDB.open(options.databaseName ?? "tattoo-stencil", Math.max(options.version ?? STORAGE_SCHEMA_VERSION, STORAGE_SCHEMA_VERSION));
    request.addEventListener("upgradeneeded", () => {
      for (const name of STORAGE_STORES) {
        if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name);
      }
    });
    return requestResult(request).then((database) => new IndexedDbStorageAdapter(database));
  }

  async transaction<T>(
    stores: readonly StorageStoreName[],
    mode: TransactionMode,
    operation: (transaction: StorageTransaction) => Promise<T>,
  ): Promise<T> {
    if (stores.length === 0) throw new Error("At least one store is required");
    const nativeTransaction = this.#database.transaction([...new Set(stores)], mode);
    const completion = transactionComplete(nativeTransaction);
    const store = (name: StorageStoreName): IDBObjectStore => nativeTransaction.objectStore(name);
    const transaction: StorageTransaction = {
      get: async <V>(name: StorageStoreName, key: string) =>
        (await requestResult(store(name).get(key))) as V | undefined,
      getAll: async <V>(name: StorageStoreName) => (await requestResult(store(name).getAll())) as V[],
      put: async <V>(name: StorageStoreName, key: string, value: V) => {
        await requestResult(store(name).put(value, key));
      },
      delete: async (name: StorageStoreName, key: string) => {
        await requestResult(store(name).delete(key));
      },
    };
    try {
      const result = await operation(transaction);
      await completion;
      return result;
    } catch (error) {
      try {
        nativeTransaction.abort();
      } catch {
        // The transaction may already have aborted; the original failure is more useful.
      }
      void completion.catch(() => undefined);
      throw error;
    }
  }

  close(): void {
    this.#database.close();
  }
}
